'use client'

import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@planner/ui'
import { Input } from '@planner/ui'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@planner/ui'
import type { MenuPackage } from '@planner/types'
import { PackageCategoryManager } from './PackageCategoryManager'

interface MenuPackageFormProps {
  pkg?: MenuPackage | null
  onClose: () => void
}

export function MenuPackageForm({ pkg, onClose }: MenuPackageFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_per_person: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedPackageId, setSavedPackageId] = useState<string | null>(pkg?.id || null)

  useEffect(() => {
    if (pkg) {
      setFormData({
        name: pkg.name,
        description: pkg.description || '',
        price_per_person: pkg.price_per_person.toString(),
      })
    }
  }, [pkg])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const packageData = {
        name: formData.name,
        description: formData.description || null,
        price_per_person: parseFloat(formData.price_per_person),
      }

      if (pkg) {
        const { error: updateError } = await supabase
          .from('menu_packages')
          .update(packageData as unknown as never)
          .eq('id', pkg.id)

        if (updateError) throw updateError
        setSavedPackageId(pkg.id)
      } else {
        const { data: insertedData, error: insertError } = await supabase
          .from('menu_packages')
          .insert([packageData] as unknown as never)
          .select()
          .single()

        if (insertError) throw insertError
        if (insertedData) {
          setSavedPackageId((insertedData as MenuPackage).id)
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle>{pkg ? 'Edytuj pakiet' : 'Dodaj pakiet menu'}</CardTitle>
        <CardDescription>
          {pkg ? 'Edytuj szczegóły pakietu' : 'Utwórz nowy pakiet menu'}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 md:p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">
                Nazwa pakietu *
              </label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="price_per_person" className="text-sm font-medium">
                Cena za osobę (zł) *
              </label>
              <Input
                id="price_per_person"
                type="number"
                step="0.01"
                min="0"
                value={formData.price_per_person}
                onChange={(e) => setFormData({ ...formData, price_per_person: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium">
                Opis
              </label>
              <textarea
                id="description"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 p-4 rounded-md">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Anuluj
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Zapisywanie...' : 'Zapisz'}
              </Button>
            </div>
          </form>
        </CardContent>
    </Card>

    {savedPackageId && (
      <div className="mt-6">
        <PackageCategoryManager packageId={savedPackageId} />
      </div>
    )}
    </>
  )
}

